/* eslint-disable camelcase */
import * as React from 'react'
import styled from 'styled-components'
import { useImmer } from 'use-immer'
import { useHistory } from 'react-router-dom'
import { useDebouncedCallback } from 'use-debounce'
import { Form as AntForm, Input, Button, notification } from 'antd'
import { Helmet } from 'react-helmet'
import { isEmpty, toLower, set } from 'lodash'
import { DEBOUNCE, tryParseQueryString } from 'helpers/utils'
import { setStorageItem, getStorageItem } from 'helpers/localStorage'
import { showError, showValidationError } from 'helpers/errors'
import { getSessionItem, removeSessionItem } from 'helpers/sessionStorage'
import { LIGHT_GREY } from 'options/colors'
import { INACTIVITY_SESSION_KEY } from 'options/auth'
import { sendClientLog } from 'helpers/auth'
import { t } from 'helpers/i18n'
import { stopEvent } from 'helpers/events'
import Modal from 'elements/Modal'
import { getTheme } from 'helpers/themes'
import Icon from 'elements/Icon'
import ForgotPassword from 'containers/ForgotPassword'
import ExternalApproval from 'containers/ExternalApproval'

const theme = getTheme()

const getStorageKey = () => 'login.form'

const Container = styled.div`
  height: 100vh;
  position: relative;
  display: flex;
  background: url(${theme.login.background}) no-repeat center;

  @media screen and (min-width: 768px) {
    display: block;
  }

  .ant-btn-link {
    color: ${theme.login.primaryColor};
  }

  .ant-input:focus,
  .ant-input:hover {
    border-color: ${theme.login.primaryColor};
  }

  .ant-input-affix-wrapper:hover {
    border-color: ${theme.login.primaryColor};
  }
`

const Form = styled(AntForm)`
  width: 336px;
  position: relative;
  margin: auto;

  input {
    color: #666;
    background-color: transparent;

    &::-webkit-input-placeholder {
      color: #bbb;
    }

    &::-moz-placeholder {
      color: #bbb;
    }

    &:-ms-input-placeholder {
      color: #bbb;
    }

    &:-moz-placeholder {
      color: #bbb;
    }
  }

  @media screen and (min-width: 768px) {
    position: absolute;
    left: ${theme.login.formPosition};
    top: 50%;
    transform: translate(-50%, -50%);
  }

  .ant-input {
    border: none;
    border-radius: 0;
    border-bottom: 1px solid rgb(217, 217, 217);
    padding-left: 40px !important;

    &:focus {
      box-shadow: none;
      border-bottom: 1px solid ${theme.login.primaryColor};
    }
  }

  .ant-form-explain {
    padding-left: 40px;
  }
`

const Buttons = styled(Form.Item)`
  padding-top: 24px;
  text-align: center;

  .ant-btn-primary {
    width: 297px;
    height: 54px;
    padding: 12px 0;
    font-weight: 600;
    border-color: ${theme.login.primaryColor};
    background-color: ${theme.login.primaryColor};
  }
`

function Component(props) {
  const history = useHistory()

  const {
    id: startupEntityId,
    redirect: startupEntityName,
    reject,
    approve,
    acknowledge,
    customerid,
    customerId,
    customerID,
    customer_id,
  } = tryParseQueryString(
    tryParseQueryString(window.location.search).code
      ? tryParseQueryString(window.location.search).state
      : window.location.search,
  )

  const injectedTenant = customerid || customerId || customerID || customer_id

  const [state, updateState] = useImmer({
    ...getStorageItem(getStorageKey(), {}),
    ...(injectedTenant ? { tenant: injectedTenant } : {}),
  })

  function setState(path, value) {
    updateState((draft) => {
      set(draft, path, value)
    })
  }

  // ssoEnabled: 1 (or true) = SSO for every user in the tenant, 2 = SSO decided per user
  const tenantSsoMode = state.tenantInfo?.ssoEnabled
  const isUserSsoMode = tenantSsoMode === 2
  const useSso = isUserSsoMode ? state.userSsoEnabled === true : Boolean(tenantSsoMode)
  const showUserName = isUserSsoMode || !tenantSsoMode
  const showPassword = isUserSsoMode ? state.userSsoEnabled === false : !tenantSsoMode
  const checkingUserSso = isUserSsoMode && !isEmpty(state.userName) && state.userSsoEnabled == null

  function handleSubmit(e) {
    stopEvent(e)

    notification.destroy()

    props.form.validateFields(async (errors, values) => {
      if (!isEmpty(errors)) {
        showValidationError()
        return
      }

      if (useSso) {
        // Okta requires a non-empty state; it carries the original query string back to /login/callback
        window.location.href = `${state.tenantInfo?.authenticationRedirectUrl}&state=${encodeURIComponent(
          window.location.search.replace(/^\?/, '') || 'login',
        )}`
      } else {
        try {
          setState('loading', true)

          const startPage = await props.login({
            ...values,
            tenant: toLower(values.tenant),
            startupEntityName,
            startupEntityId,
          })

          await sendClientLog('Success', 'Login', { startPage })

          history.push(startPage)
        } catch (error) {
          showError({ error })
          setState('loading', false)
        }
      }
    })
  }

  React.useEffect(() => {
    if (getSessionItem(INACTIVITY_SESSION_KEY)) {
      Modal.info({
        title: t('inactivityWarningMessage'),
        content: t('inactivityWarningDescription'),
      })

      removeSessionItem(INACTIVITY_SESSION_KEY)
    }
  }, [])

  React.useEffect(() => {
    setStorageItem(getStorageKey(), { tenant: toLower(state.tenant) })
  }, [state.tenant])

  const fetchTenantInfo = useDebouncedCallback(async (subdomain) => {
    try {
      const response = await props.getTenantInfo({ subdomain })
      setState('tenantInfo', response.value.data)
    } catch (error) {
      console.warn(error)
      setState('tenantInfo', null)
    }
  }, DEBOUNCE)

  React.useEffect(() => {
    if (state.tenant) {
      fetchTenantInfo(toLower(state.tenant))
    } else {
      setState('tenantInfo', null)
      fetchTenantInfo.cancel()
    }
  }, [state.tenant])

  const fetchUserSsoEnabled = useDebouncedCallback(async (tenant, userName) => {
    try {
      const response = await props.getSsoEnabled({ tenant, userName })
      setState('userSsoEnabled', response.value.data === true)
    } catch (error) {
      console.warn(error)
      setState('userSsoEnabled', false)
    }
  }, DEBOUNCE)

  React.useEffect(() => {
    setState('userSsoEnabled', null)

    if (isUserSsoMode && state.tenant && state.userName) {
      fetchUserSsoEnabled(toLower(state.tenant), state.userName)
    } else {
      fetchUserSsoEnabled.cancel()
    }
  }, [isUserSsoMode, state.tenant, state.userName])

  React.useEffect(() => {
    async function tryLoginSso() {
      const { code } = tryParseQueryString(window.location.search)

      if (code) {
        try {
          setState('loading', true)

          const startPage = await props.login({
            tenant: toLower(state.tenant),
            code,
            redirectUri: `${window.location.origin}${window.location.pathname}`,
            startupEntityName,
            startupEntityId,
          })

          await sendClientLog('Success', 'Login SSO', { startPage })

          history.push(startPage)
        } catch (error) {
          showError({ error })
          setState('loading', false)
        }
      }
    }

    if (window.location.search) {
      tryLoginSso()
    }
  }, [window.location.search])

  return (
    <Container>
      <Helmet>
        <title>TRMS</title>
      </Helmet>
      <Form layout="vertical" colon={false} onSubmit={handleSubmit}>
        <Form.Item>
          {props.form.getFieldDecorator('tenant', {
            rules: [{ required: true, message: t('requiredField') }],
            initialValue: state.tenant,
          })(
            <Input
              name="tenant"
              prefix={<Icon type="Store" color={LIGHT_GREY} />}
              placeholder={t('customerId')}
              autoFocus={!state.tenant}
              onChange={(e) => setState('tenant', e.target.value)}
              autoCorrect="off"
              autoCapitalize="none"
              disabled={state.loading}
              allowClear
            />,
          )}
        </Form.Item>
        {showUserName && (
          <Form.Item>
            {props.form.getFieldDecorator('userName', {
              rules: [{ required: true, message: t('requiredField') }],
            })(
              <Input
                name="userName"
                prefix={<Icon type="Person" color={LIGHT_GREY} />}
                placeholder={t('userId')}
                autoFocus={Boolean(state.tenant)}
                onChange={(e) => setState('userName', e.target.value)}
                autoCorrect="off"
                autoCapitalize="none"
                disabled={state.loading}
                allowClear
              />,
            )}
          </Form.Item>
        )}
        {showPassword && (
          <Form.Item>
            {props.form.getFieldDecorator('password', {
              rules: [{ required: true, message: t('requiredField') }],
            })(
              <Input.Password
                name="password"
                type="password"
                prefix={<Icon type="Lock" color={LIGHT_GREY} />}
                placeholder={t('password')}
                onChange={(e) => setState('password', e.target.value)}
                disabled={state.loading}
              />,
            )}
          </Form.Item>
        )}
        <Buttons>
          <Button type="primary" htmlType="submit" loading={state.loading || checkingUserSso} block>
            {useSso ? t('loginUsingSSO') : t('login')}
          </Button>
        </Buttons>
        {showPassword && (
          <div className="text-center">
            <ForgotPassword
              initialValues={{
                tenant: props.form.getFieldValue('tenant'),
                userName: props.form.getFieldValue('userName'),
              }}
            />
          </div>
        )}
      </Form>
      {!(isEmpty(approve) && isEmpty(reject) && isEmpty(acknowledge)) && (
        <ExternalApproval approve={approve} reject={reject} acknowledge={acknowledge} />
      )}
    </Container>
  )
}

export default Form.create()(Component)
